import type { SystemRolePermissionId } from '@clockwise/shared';
import { useMe } from './me';

/**
 * Returns true iff the calling user has the given permission id.
 *
 * Reads from the cached `/api/users/me` payload — no extra request. While the
 * payload is loading the hook returns `false`; callers that want to suppress
 * a flash of "no access" UI should check `useMe().loading` separately.
 *
 * Presence-only: scope ids declared in the registry (`all`, `customer_team`,
 * `managed`, `assigned`) are stored on the role but not yet enforced. Adding
 * the entry at any scope grants the permission today. Don't conflate this
 * with the future scope-aware filter.
 */
export function useHasPermission(id: SystemRolePermissionId): boolean {
  const { permissions } = useMe();
  return permissions.has(id);
}

/**
 * Reads multiple permissions at once. Cheaper than calling `useHasPermission`
 * N times when a screen gates many UI elements.
 */
export function useHasPermissions<TIds extends readonly SystemRolePermissionId[]>(
  ids: TIds,
): Record<TIds[number], boolean> {
  const { permissions } = useMe();
  const out: Partial<Record<TIds[number], boolean>> = {};
  for (const id of ids) {
    (out as Record<string, boolean>)[id] = permissions.has(id);
  }
  return out as Record<TIds[number], boolean>;
}
